import { getDeployOptions } from '../utils/environment';

export interface ApiCallEvent {
  endpoint: string;
  ms: number;
  method?: string;
  status?: number;
  request_id?: string;
  request_bytes?: number;
  response_bytes?: number;
  feature_flags?: Record<string, boolean>;
  errorCode?: string;
  timeout?: boolean;
}

interface PhaseEvent {
  phase: string;
  duration_ms: number;
  order_type?: string;
  order_id?: number;
  order_size_bucket?: OrderSizeBucket;
  client_id?: number;
  project_count?: number;
}

export type OrderSizeBucket = 'small' | 'medium' | 'large';

const OPEN_EVENT = 'item_order_open_phases';
const SAVE_EVENT = 'item_order_save_phases';
const CLIENT_EVENT = 'client_load_phases';
const API_EVENT = 'api_call';

export function logApiCall(event: ApiCallEvent): void {
  emit(API_EVENT, event, event.ms);
}

export function logOpenPhase(event: PhaseEvent): void {
  emit(OPEN_EVENT, event, event.duration_ms);
}

export function logSavePhase(event: PhaseEvent): void {
  emit(SAVE_EVENT, event, event.duration_ms);
}

export function logClientPhase(event: PhaseEvent): void {
  emit(CLIENT_EVENT, event, event.duration_ms);
}

function emit(name: string, payload: object, value: number | null = null): void {
  try {
    const opts = getDeployOptions();
    const debugForced = (window as any).__perfDebug === true;
    if (debugForced || (opts && opts.allowLogger)) {
      // eslint-disable-next-line no-console
      console.log(`[perf] ${name}`, payload);
    }
  } catch {
    /* deploy options may not be populated very early in boot */
  }
  try {
    const client = (window as any).statsigClient;
    if (client && typeof client.logEvent === 'function') {
      client.logEvent(name, value, stringifyMetadata(payload));
    }
  } catch {
    /* telemetry must never break the app */
  }
}

function stringifyMetadata(obj: object): Record<string, string> {
  const out: Record<string, string> = {};
  const record = obj as Record<string, unknown>;
  for (const key of Object.keys(record)) {
    const v = record[key];
    if (v === undefined || v === null) continue;
    if (typeof v === 'number' || typeof v === 'boolean') out[key] = String(v);
    else if (typeof v === 'string') out[key] = v;
    else {
      try {
        out[key] = JSON.stringify(v);
      } catch {
        /* skip unserializable */
      }
    }
  }
  return out;
}

/**
 * Bucket an order by its rough complexity so phase events stay comparable across users.
 * Bucket boundaries are coarse on purpose — we want stable buckets, not precise sizes.
 */
export function bucketOrderSize(counts: {
  fabrics?: number;
  embellishments?: number;
  uploads?: number;
}): OrderSizeBucket {
  const total = (counts.fabrics || 0) + (counts.embellishments || 0) + (counts.uploads || 0);
  if (total <= 5) return 'small';
  if (total <= 20) return 'medium';
  return 'large';
}

export interface Stopwatch {
  end(): number;
}

export function startStopwatch(): Stopwatch {
  const start = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return {
    end() {
      const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
      return now - start;
    },
  };
}

/**
 * Normalize a URL to an endpoint shape suitable for low-cardinality telemetry.
 * Strips the origin, query string, and obvious numeric IDs.
 */
export function normalizeEndpoint(url: string | undefined): string {
  if (!url) return '<unknown>';
  let path = url;
  const qIdx = path.indexOf('?');
  if (qIdx >= 0) path = path.slice(0, qIdx);
  // Strip protocol+host if present
  const protoIdx = path.indexOf('://');
  if (protoIdx >= 0) {
    const slash = path.indexOf('/', protoIdx + 3);
    path = slash >= 0 ? path.slice(slash) : '/';
  }
  // Collapse path segments that look like numeric IDs
  return path.replace(/\/\d+(?=\/|$)/g, '/:id');
}

// ---------------------------------------------------------------------------
// Dev tools: persistent flag toggles + console helpers.
// Open DevTools and run:
//   perf.on()            // enable all tracked perf flags
//   perf.off()           // disable all tracked perf flags
//   perf.status()        // print current local overrides
//   perf.clear()         // remove all local overrides (fall back to Statsig)
//   perf.on('perf_calc_parallel_save')   // toggle a single flag
// Overrides persist in localStorage and survive refresh. Refresh after
// changing flags so the new code path takes effect on the next open/save.
// ---------------------------------------------------------------------------
const PERF_FLAGS_STORAGE_KEY = '__quipa_perf_flags';
const TRACKED_PERF_FLAGS = [
  'perf_calc_parallel_open',
  'perf_calc_parallel_save',
  'perf_skip_auto_pdf_preload',
  'perf_calc_client_cache',
  'perf_skip_clean_drawing_save',
  'perf_calc_client_room_cache',
  'perf_calc_section_dirty',
  'perf_client_page_parallel_cache',
  'perf_backend_request_telemetry',
  'perf_client_minimal_reads',
  'perf_order_initial_endpoint',
  'perf_order_open_bundle',
  'perf_save_bundle_api',
];

function readLocalPerfFlags(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(PERF_FLAGS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeLocalPerfFlags(flags: Record<string, boolean>): void {
  try {
    localStorage.setItem(PERF_FLAGS_STORAGE_KEY, JSON.stringify(flags));
  } catch {
    /* localStorage may be disabled */
  }
}

export function getPerfFlagOverridesSnapshot(): Record<string, boolean> {
  const flags = readLocalPerfFlags();
  const out: Record<string, boolean> = {};
  for (const name of TRACKED_PERF_FLAGS) {
    if (typeof flags[name] === 'boolean') {
      out[name] = flags[name];
    }
  }
  return out;
}

function patchStatsigClientForPerfFlags(): boolean {
  const w = window as any;
  const client = w.statsigClient;
  if (!client || typeof client.checkGate !== 'function' || client.__perfPatched) {
    return false;
  }
  const original = client.checkGate.bind(client);
  client.checkGate = (name: string) => {
    const flags = readLocalPerfFlags();
    if (typeof flags[name] === 'boolean') {
      return flags[name];
    }
    return original(name);
  };
  client.__perfPatched = true;
  return true;
}

function installPerfDevTools(): void {
  if (typeof window === 'undefined') return;
  const w = window as any;
  if (w.perf) return; // already installed

  w.perf = {
    on(...names: string[]) {
      const flags = readLocalPerfFlags();
      const targets = names.length > 0 ? names : TRACKED_PERF_FLAGS;
      for (const n of targets) flags[n] = true;
      writeLocalPerfFlags(flags);
      // eslint-disable-next-line no-console
      console.log('[perf-dev] ENABLED:', targets, '→ refresh to apply');
    },
    off(...names: string[]) {
      const flags = readLocalPerfFlags();
      const targets = names.length > 0 ? names : TRACKED_PERF_FLAGS;
      for (const n of targets) flags[n] = false;
      writeLocalPerfFlags(flags);
      // eslint-disable-next-line no-console
      console.log('[perf-dev] DISABLED:', targets, '→ refresh to apply');
    },
    status() {
      const flags = readLocalPerfFlags();
      // eslint-disable-next-line no-console
      console.log('[perf-dev] local overrides:', flags);
      return flags;
    },
    clear() {
      try {
        localStorage.removeItem(PERF_FLAGS_STORAGE_KEY);
      } catch {
        /* no-op */
      }
      // eslint-disable-next-line no-console
      console.log('[perf-dev] cleared local overrides → refresh to apply');
    },
  };

  // Apply the override as soon as the Statsig client exists. Poll briefly
  // since this module loads before initStatsig may complete.
  if (patchStatsigClientForPerfFlags()) return;
  const intervalId = window.setInterval(() => {
    if (patchStatsigClientForPerfFlags()) {
      window.clearInterval(intervalId);
    }
  }, 100);
  window.setTimeout(() => window.clearInterval(intervalId), 15000);
}

installPerfDevTools();
